import React, { FunctionComponent } from 'react';
import { GTM_GA4_Ecommerce_Purchase, GTM_GA4_Ecommerce_Select_Item, GTM_Page_Curso_Livre } from 'components/GoogleTagManager';
import { bucketS3, caminhoAnexosCursosLivres, caminhoAnexosAuthoringTool } from 'constants/EnvironmentData';
import keycloak from 'core/config/keycloak';
import useLogged from 'hooks/useLogged';
import AdminCursoLivre from 'models/admin/AdminCursoLivre';
import { useHistory } from 'react-router-dom';
import GenericComponentsUtils from 'pages/generic-components/generic-components.utils';
import TipoRedirecionamento from 'enums/TipoRedirecionamento';
import { getLoginRedirectUri } from 'core/util/StorageRedirectRulesHelper';
import {
    BottomContainer,
    ButtonsContainer,
    CardTitle,
    Center,
    ContentContainer,
    CursoLivreCardContainer,
    ImageContainer,
    Img,
    PrimaryYellowButton,
    ProgressBarContainer,
    ProgressBarFill,
    ProgressText,
    SecondaryBlueButton,
    StatusContainer,
    StatusLabel,
} from './CursoLivreCard.styled';

type CursoLivreCardData = AdminCursoLivre & {
    isExclusivo?: boolean;
    isMatriculado?: boolean;
    isConcluido?: boolean;
    progresso?: number;
};

interface ICursoLivreCard {
    curso: CursoLivreCardData;
    isContinueSection?: boolean;
    isNovo?: boolean;
}

const isFadCurso = (curso: CursoLivreCardData): boolean => {
    return curso.isExclusivo === true;
};

const CursoLivreCard: FunctionComponent<ICursoLivreCard> = ({ ...props }: ICursoLivreCard) => {
    const { authenticated } = useLogged();
    const history = useHistory();

    const matriculado = props.curso.isMatriculado ?? false;
    const concluido = props.curso.isConcluido ?? false;
    const progresso = props.curso.progresso ?? 0;

    const getS3BaseUrl = (): string => {
        if (isFadCurso(props.curso)) {
            return bucketS3() + caminhoAnexosAuthoringTool();
        }

        return bucketS3() + caminhoAnexosCursosLivres();
    };

    const openUrl = (url: string) => {
        GenericComponentsUtils.gotoLink(
            url,
            history,
            keycloak.authenticated ?? false,
            {
                tipoRedirecionamento: TipoRedirecionamento.CADASTRO,
            },
            {
                tipoRedirecionamento: TipoRedirecionamento.IGNORAR,
            }
        );
    };

    const accessDenied = !props.curso.naoAplicavel ? props.curso.emProducao : props.curso.emProducao;

    const isFad = isFadCurso(props.curso);

    const isConcluidoFad = isFad && concluido;

    const cardBackgroundColor = isConcluidoFad ? '#00A8E9' : isFad ? '#FFCA00' : '#F4F4F4';

    const progressColor = isFad ? (isConcluidoFad ? '#FFCA00' : '#003C78') : concluido ? '#003C78' : '#FFCA00';

    const isNovo = props.isNovo ?? false;

    let tagText: string | null = null;

    if (isNovo) {
        tagText = 'Esse curso é novo';
    } else if (isFad && props.isContinueSection) {
        tagText = 'Curso exclusivo da sua rede';
    } else if (!isFad) {
        tagText = 'Cursos livres';
    }

    const getCourseUrl = () => {
        return isFad ? `/cursos${props.curso.url}` : props.curso.url;
    };

    const handleStartClick = () => {
        if (!authenticated) {
            const registerUrl = keycloak.createRegisterUrl({
                redirectUri: getLoginRedirectUri(),
            });
            location.assign(registerUrl);
            return;
        }

        GTM_Page_Curso_Livre(props.curso.codigo, props.curso.titulo, props.curso.descricao);

        GTM_GA4_Ecommerce_Select_Item(props.curso.codigo.toString(), props.curso.titulo);

        GTM_GA4_Ecommerce_Purchase(keycloak.subject ?? '', props.curso.codigo.toString(), props.curso.titulo);

        openUrl(getCourseUrl());
    };

    const handleAboutClick = () => {
        openUrl(getCourseUrl());
    };

    return (
        <CursoLivreCardContainer backgroundColor={cardBackgroundColor}>
            {tagText && (
                <StatusContainer isExclusivo={isFad && !isNovo} isNovo={isNovo}>
                    <StatusLabel>{tagText}</StatusLabel>
                </StatusContainer>
            )}

            <ImageContainer hasTag={!!tagText} onClick={handleAboutClick}>
                <Img hasTag={!!tagText} src={getS3BaseUrl() + props.curso.filePath} loading="lazy" decoding="async" />
            </ImageContainer>

            <ContentContainer backgroundColor={cardBackgroundColor}>
                <CardTitle>{props.curso.titulo}</CardTitle>

                <BottomContainer>
                    <Center $alignStart>
                        <>
                            {isFad ? (
                                <>
                                    {concluido ? (
                                        <>
                                            <ProgressText>Você já concluiu esse curso</ProgressText>

                                            <ProgressBarContainer>
                                                <ProgressBarFill percent={100} backgroundColor={progressColor} />
                                            </ProgressBarContainer>

                                            <SecondaryBlueButton onClick={handleAboutClick}>Acessar curso</SecondaryBlueButton>
                                        </>
                                    ) : (
                                        <>
                                            <ProgressText>Progresso: {progresso}%</ProgressText>

                                            <ProgressBarContainer>
                                                <ProgressBarFill percent={progresso} backgroundColor={progressColor} />
                                            </ProgressBarContainer>

                                            <SecondaryBlueButton onClick={handleStartClick}>Acessar curso</SecondaryBlueButton>
                                        </>
                                    )}
                                </>
                            ) : concluido ? (
                                <>
                                    <ProgressText>Você já concluiu esse curso</ProgressText>

                                    <ProgressBarContainer>
                                        <ProgressBarFill percent={100} backgroundColor={progressColor} />
                                    </ProgressBarContainer>

                                    <SecondaryBlueButton onClick={handleAboutClick}>Acessar curso</SecondaryBlueButton>
                                </>
                            ) : matriculado ? (
                                <>
                                    <ProgressText>Progresso: {progresso}%</ProgressText>

                                    <ProgressBarContainer>
                                        <ProgressBarFill percent={progresso} backgroundColor={progressColor} />
                                    </ProgressBarContainer>

                                    <PrimaryYellowButton onClick={handleStartClick}>Continuar curso</PrimaryYellowButton>
                                </>
                            ) : (
                                <ButtonsContainer>
                                    <PrimaryYellowButton onClick={handleStartClick} disabled={accessDenied}>
                                        Iniciar curso
                                    </PrimaryYellowButton>

                                    {/* <SecondaryBlueButton onClick={handleAboutClick}>Sobre o curso</SecondaryBlueButton> */}
                                </ButtonsContainer>
                            )}
                        </>
                    </Center>
                </BottomContainer>
            </ContentContainer>
        </CursoLivreCardContainer>
    );
};

export default CursoLivreCard;
